import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Grid,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Person,
  Lock,
  Notifications,
  Help,
  Email,
  Phone,
  Business,
  Save,
  Cancel,
  PhotoCamera,
  Security,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Info,
  ContactSupport,
  Description,
  PrivacyTip,
  Gavel,
  AdminPanelSettings,
  PersonAdd,
  Delete,
} from '@mui/icons-material';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, updateDoc, collection, getDocs, query, where, orderBy, deleteDoc } from 'firebase/firestore';
import { auth, db, functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';

interface SettingsProps {
  userType: 'venue_admin' | 'super_admin';
  userData: any;
  onUpdate?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings: React.FC<SettingsProps> = ({ userType, userData, onUpdate }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Profile state
  const [profileData, setProfileData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
    company: userData?.company || '',
  });

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailNotifications: userData?.preferences?.emailNotifications ?? true,
    pushNotifications: userData?.preferences?.pushNotifications ?? true,
    offerUpdates: userData?.preferences?.offerUpdates ?? true,
    analyticsReports: userData?.preferences?.analyticsReports ?? true,
    systemAlerts: userData?.preferences?.systemAlerts ?? true,
  });

  // Support dialog
  const [supportDialog, setSupportDialog] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');

  // Super Admin Management state (super_admin only)
  const [superAdmins, setSuperAdmins] = useState<any[]>([]);
  const [superAdminsLoading, setSuperAdminsLoading] = useState(false);
  const [createSuperAdminDialog, setCreateSuperAdminDialog] = useState(false);
  const [deleteSuperAdminDialog, setDeleteSuperAdminDialog] = useState<any | null>(null);
  const [newSuperAdmin, setNewSuperAdmin] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showNewAdminPasswords, setShowNewAdminPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setError('');
    setSuccess('');
  };

  // Profile Management
  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      setError('');

      if (!userData?.adminId) {
        throw new Error('User ID not found');
      }

      const collectionName = userType === 'venue_admin' ? 'venue_admins' : 'super_admins';
      const userQuery = await import('firebase/firestore').then(mod => 
        mod.query(mod.collection(db, collectionName), mod.where('adminId', '==', userData.adminId))
      );
      
      const snapshot = await import('firebase/firestore').then(mod => mod.getDocs(userQuery));
      
      if (snapshot.empty) {
        throw new Error('User document not found');
      }

      const userDocRef = doc(db, collectionName, snapshot.docs[0].id);
      
      await updateDoc(userDocRef, {
        name: profileData.name,
        phone: profileData.phone,
        company: profileData.company,
        updatedAt: new Date(),
      });

      setSuccess('Profile updated successfully');
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Password Change
  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate passwords
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        throw new Error('All password fields are required');
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters');
      }

      if (passwordData.newPassword === passwordData.currentPassword) {
        throw new Error('New password must be different from current password');
      }

      // Re-authenticate user
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No authenticated user found');
      }

      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      setSuccess('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      console.error('Error changing password:', err);
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Notification Preferences
  const handleNotificationUpdate = async () => {
    try {
      setLoading(true);
      setError('');

      if (!userData?.adminId) {
        throw new Error('User ID not found');
      }

      const collectionName = userType === 'venue_admin' ? 'venue_admins' : 'super_admins';
      const userQuery = await import('firebase/firestore').then(mod => 
        mod.query(mod.collection(db, collectionName), mod.where('adminId', '==', userData.adminId))
      );
      
      const snapshot = await import('firebase/firestore').then(mod => mod.getDocs(userQuery));
      
      if (snapshot.empty) {
        throw new Error('User document not found');
      }

      const userDocRef = doc(db, collectionName, snapshot.docs[0].id);
      
      await updateDoc(userDocRef, {
        'preferences.emailNotifications': notifications.emailNotifications,
        'preferences.pushNotifications': notifications.pushNotifications,
        'preferences.offerUpdates': notifications.offerUpdates,
        'preferences.analyticsReports': notifications.analyticsReports,
        'preferences.systemAlerts': notifications.systemAlerts,
        updatedAt: new Date(),
      });

      setSuccess('Notification preferences updated');
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Error updating notifications:', err);
      setError(err.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  // Support Request
  const handleSupportSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!supportMessage.trim()) {
        throw new Error('Please enter a message');
      }

      // Here you would typically send this to a support system or create a Firestore document
      const supportRequest = {
        userId: userData?.adminId,
        userType,
        name: userData?.name,
        email: userData?.email,
        message: supportMessage,
        timestamp: new Date(),
        status: 'open',
      };

      // Save to Firestore (you can create a 'support_tickets' collection)
      await import('firebase/firestore').then(mod => 
        mod.addDoc(mod.collection(db, 'support_tickets'), supportRequest)
      );

      setSuccess('Support request submitted successfully. We\'ll get back to you soon!');
      setSupportMessage('');
      setSupportDialog(false);
    } catch (err: any) {
      console.error('Error submitting support request:', err);
      setError(err.message || 'Failed to submit support request');
    } finally {
      setLoading(false);
    }
  };

  // ── Super Admin Management (super_admin only) ──────────────────────────────

  const loadSuperAdmins = async () => {
    try {
      setSuperAdminsLoading(true);
      const q = query(collection(db, 'super_admins'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const admins = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSuperAdmins(admins);
    } catch (err: any) {
      console.error('Error loading super admins:', err);
      setError('Failed to load super admins');
    } finally {
      setSuperAdminsLoading(false);
    }
  };

  const handleCreateSuperAdmin = async () => {
    try {
      setLoading(true);
      setError('');

      if (!newSuperAdmin.name.trim() || !newSuperAdmin.email.trim() || !newSuperAdmin.password) {
        throw new Error('All fields are required');
      }
      if (newSuperAdmin.password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      if (newSuperAdmin.password !== newSuperAdmin.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const createSuperAdminFn = httpsCallable(functions, 'createSuperAdmin');
      await createSuperAdminFn({
        name: newSuperAdmin.name.trim(),
        email: newSuperAdmin.email.trim(),
        password: newSuperAdmin.password,
      });

      setSuccess(`Super admin account created for ${newSuperAdmin.email}`);
      setNewSuperAdmin({ name: '', email: '', password: '', confirmPassword: '' });
      setCreateSuperAdminDialog(false);
      await loadSuperAdmins();
    } catch (err: any) {
      console.error('Error creating super admin:', err);
      if (err.code === 'functions/already-exists') {
        setError('An account with this email already exists');
      } else {
        setError(err.message || 'Failed to create super admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSuperAdmin = async () => {
    if (!deleteSuperAdminDialog) return;
    try {
      setLoading(true);
      setError('');
      if (deleteSuperAdminDialog.adminId === userData?.adminId) {
        throw new Error('You cannot delete your own account');
      }
      const deleteSuperAdminFn = httpsCallable(functions, 'deleteSuperAdmin');
      await deleteSuperAdminFn({ adminId: deleteSuperAdminDialog.adminId });
      setSuccess(`Super admin ${deleteSuperAdminDialog.name} has been removed`);
      setDeleteSuperAdminDialog(null);
      await loadSuperAdmins();
    } catch (err: any) {
      console.error('Error deleting super admin:', err);
      setError(err.message || 'Failed to delete super admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account settings and preferences
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Settings Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab icon={<Person />} label="Profile" />
            <Tab icon={<Lock />} label="Security" />
            <Tab icon={<Notifications />} label="Notifications" />
            <Tab icon={<Help />} label="Help & Support" />
            {userType === 'super_admin' && (
              <Tab
                icon={<AdminPanelSettings />}
                label="Super Admins"
                onClick={() => {
                  if (superAdmins.length === 0) loadSuperAdmins();
                }}
              />
            )}
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={currentTab} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Profile Photo Section */}
              <Grid size={{ xs: 12 }}>
                <Box display="flex" alignItems="center" gap={3}>
                  <Avatar
                    sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}
                  >
                    {userData?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {userData?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {userData?.email}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCamera />}
                      disabled
                    >
                      Upload Photo (Coming Soon)
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider />
              </Grid>

              {/* Profile Form */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={profileData.email}
                  disabled
                  helperText="Email cannot be changed"
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Company"
                  value={profileData.company}
                  onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                  InputProps={{
                    startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => setProfileData({
                      name: userData?.name || '',
                      email: userData?.email || '',
                      phone: userData?.phone || '',
                      company: userData?.company || '',
                    })}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleProfileUpdate}
                    disabled={loading}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={currentTab} index={1}>
          <CardContent>
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Security /> Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update your password to keep your account secure
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  error={passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword}
                  helperText={
                    passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info" icon={<Info />}>
                  <strong>Password Requirements:</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>At least 6 characters long</li>
                    <li>Different from your current password</li>
                    <li>Use a mix of letters, numbers, and symbols for better security</li>
                  </ul>
                </Alert>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => {
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handlePasswordChange}
                    disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                  >
                    Change Password
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={currentTab} index={2}>
          <CardContent>
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Notification Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage how you receive updates and alerts
              </Typography>
            </Box>

            <List>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive notifications via email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.emailNotifications}
                      onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <Notifications />
                </ListItemIcon>
                <ListItemText
                  primary="Push Notifications"
                  secondary="Receive push notifications on your device"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.pushNotifications}
                      onChange={(e) => setNotifications({ ...notifications, pushNotifications: e.target.checked })}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              {userType === 'venue_admin' && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle />
                    </ListItemIcon>
                    <ListItemText
                      primary="Offer Updates"
                      secondary="Get notified about offer performance and redemptions"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notifications.offerUpdates}
                          onChange={(e) => setNotifications({ ...notifications, offerUpdates: e.target.checked })}
                        />
                      }
                      label=""
                    />
                  </ListItem>

                  <Divider />
                </>
              )}

              <ListItem>
                <ListItemIcon>
                  <Info />
                </ListItemIcon>
                <ListItemText
                  primary="Analytics Reports"
                  secondary="Weekly analytics and performance summaries"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.analyticsReports}
                      onChange={(e) => setNotifications({ ...notifications, analyticsReports: e.target.checked })}
                    />
                  }
                  label=""
                />
              </ListItem>

              <Divider />

              <ListItem>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText
                  primary="System Alerts"
                  secondary="Important system updates and security alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.systemAlerts}
                      onChange={(e) => setNotifications({ ...notifications, systemAlerts: e.target.checked })}
                      disabled
                    />
                  }
                  label=""
                />
                <Chip label="Required" size="small" color="primary" />
              </ListItem>
            </List>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleNotificationUpdate}
                disabled={loading}
              >
                Save Preferences
              </Button>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Help & Support Tab */}
        <TabPanel value={currentTab} index={3}>
          <CardContent>
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Help & Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get help and access resources
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Quick Actions */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box textAlign="center">
                      <ContactSupport sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Contact Support
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Get help from our support team
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setSupportDialog(true)}
                      >
                        Send Message
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box textAlign="center">
                      <Description sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Documentation
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Browse guides and tutorials
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        href="https://docs.jamapp.com"
                        target="_blank"
                      >
                        View Docs
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box textAlign="center">
                      <Email sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Email Us
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        support@jamapp.com
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        href="mailto:support@jamapp.com"
                      >
                        Send Email
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Resources */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Resources
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <List>
                  <ListItemButton component="a" href="https://jamapp.co/terms" target="_blank">
                    <ListItemIcon>
                      <Gavel />
                    </ListItemIcon>
                    <ListItemText
                      primary="Terms of Service"
                      secondary="Read our terms and conditions"
                    />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton component="a" href="https://jamapp.co/privacy" target="_blank">
                    <ListItemIcon>
                      <PrivacyTip />
                    </ListItemIcon>
                    <ListItemText
                      primary="Privacy Policy"
                      secondary="Learn how we protect your data"
                    />
                  </ListItemButton>
                </List>
              </Grid>

              {/* System Info */}
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      System Information
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Account Type:</Typography>
                        <Chip 
                          label={userType === 'venue_admin' ? 'Venue Admin' : 'Super Admin'} 
                          size="small" 
                          color="primary"
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">User ID:</Typography>
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                          {userData?.adminId?.substring(0, 12)}...
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">App Version:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          v1.0.0
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Super Admins Tab — visible to super_admin only */}
        {userType === 'super_admin' && (
          <TabPanel value={currentTab} index={4}>
            <CardContent>
              <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <AdminPanelSettings /> Super Admin Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and manage super administrator accounts
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setCreateSuperAdminDialog(true)}
                >
                  Create Super Admin
                </Button>
              </Box>

              <Alert severity="warning" sx={{ mb: 3 }}>
                Super admins have full access to the entire dashboard. Only create accounts for trusted individuals.
              </Alert>

              {superAdminsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {superAdmins.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                              No super admins found. Click "Load" or create one above.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        superAdmins.map((admin) => (
                          <TableRow key={admin.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                  {admin.name?.charAt(0)?.toUpperCase() || '?'}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>{admin.name}</Typography>
                                  {admin.adminId === userData?.adminId && (
                                    <Chip label="You" size="small" color="primary" sx={{ ml: 0 }} />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{admin.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {admin.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={admin.adminId === userData?.adminId}
                                onClick={() => setDeleteSuperAdminDialog(admin)}
                                title={admin.adminId === userData?.adminId ? "You cannot delete your own account" : "Remove super admin"}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!superAdminsLoading && superAdmins.length === 0 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button variant="outlined" onClick={loadSuperAdmins}>
                    Load Super Admins
                  </Button>
                </Box>
              )}
            </CardContent>
          </TabPanel>
        )}
      </Card>

      {/* Support Dialog */}
      <Dialog open={supportDialog} onClose={() => setSupportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Describe your issue or question and our support team will get back to you as soon as possible.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Your Message"
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            placeholder="Please describe your issue in detail..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSupportSubmit}
            disabled={loading || !supportMessage.trim()}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Super Admin Dialog */}
      <Dialog
        open={createSuperAdminDialog}
        onClose={() => {
          setCreateSuperAdminDialog(false);
          setNewSuperAdmin({ name: '', email: '', password: '', confirmPassword: '' });
          setError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle display="flex" alignItems="center" gap={1}>
          <PersonAdd /> Create Super Admin Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            The new super admin will receive full dashboard access. Share credentials with them securely.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              fullWidth
              label="Full Name"
              value={newSuperAdmin.name}
              onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, name: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={newSuperAdmin.email}
              onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, email: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showNewAdminPasswords.password ? 'text' : 'password'}
              value={newSuperAdmin.password}
              onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, password: e.target.value })}
              helperText="Minimum 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowNewAdminPasswords({ ...showNewAdminPasswords, password: !showNewAdminPasswords.password })
                      }
                      edge="end"
                    >
                      {showNewAdminPasswords.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showNewAdminPasswords.confirmPassword ? 'text' : 'password'}
              value={newSuperAdmin.confirmPassword}
              onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, confirmPassword: e.target.value })}
              error={newSuperAdmin.confirmPassword !== '' && newSuperAdmin.password !== newSuperAdmin.confirmPassword}
              helperText={
                newSuperAdmin.confirmPassword !== '' && newSuperAdmin.password !== newSuperAdmin.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowNewAdminPasswords({
                          ...showNewAdminPasswords,
                          confirmPassword: !showNewAdminPasswords.confirmPassword,
                        })
                      }
                      edge="end"
                    >
                      {showNewAdminPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateSuperAdminDialog(false);
              setNewSuperAdmin({ name: '', email: '', password: '', confirmPassword: '' });
              setError('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSuperAdmin}
            disabled={
              loading ||
              !newSuperAdmin.name.trim() ||
              !newSuperAdmin.email.trim() ||
              !newSuperAdmin.password ||
              newSuperAdmin.password !== newSuperAdmin.confirmPassword
            }
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
          >
            {loading ? 'Creating...' : 'Create Super Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Super Admin Confirmation Dialog */}
      <Dialog
        open={!!deleteSuperAdminDialog}
        onClose={() => setDeleteSuperAdminDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Super Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{' '}
            <strong>{deleteSuperAdminDialog?.name}</strong> ({deleteSuperAdminDialog?.email}) as a super admin?
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            This will revoke their access to the dashboard immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSuperAdminDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSuperAdmin}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;